import React from 'react';
import { Box, Typography, Container, Grid, IconButton } from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import EmailIcon from '@mui/icons-material/Email';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import { useTheme } from '../../contexts/ThemeContext';

const Footer = () => {
  const location = useLocation();
  const { themeMode } = useTheme();
  const isLight = themeMode === 'light';

  // Check if we're on an authenticated page (has sidebar)
  const authenticatedRoutes = ['/dashboard', '/profile', '/settings', '/admin'];
  const hasSidebar = authenticatedRoutes.some(route => location.pathname.startsWith(route));

  return (
    <Box
      component="footer"
      sx={{
        py: { xs: 8, md: 10 },
        px: 2,
        mt: 'auto',
        ml: hasSidebar ? '280px' : 0,
        backgroundColor: isLight ? '#f8fafc' : '#0b0c0e',
        borderTop: isLight ? '1px solid #e2e8f0' : '1px solid rgba(247, 247, 248, 0.08)',
        transition: 'all 0.25s ease',
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={5}>
          {/* Brand Info & Mission Statement */}
          <Grid item xs={12} md={5}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '8px',
                  background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.2) 0%, rgba(59, 130, 246, 0.2) 100%)',
                  border: '1px solid rgba(6, 182, 212, 0.35)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <RocketLaunchIcon sx={{ fontSize: 18, color: '#22d3ee' }} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.05rem', color: isLight ? '#0f172a' : '#f7f7f8' }}>
                VerveAI
              </Typography>
              <Box
                component="span"
                sx={{
                  fontSize: '9px',
                  fontWeight: 700,
                  letterSpacing: '0.05em',
                  color: '#06b6d4',
                  bgcolor: 'rgba(6, 182, 212, 0.1)',
                  border: '1px solid rgba(6, 182, 212, 0.25)',
                  px: 0.8,
                  py: 0.1,
                  borderRadius: '4px',
                }}
              >
                STARTUP LAUNCHPAD
              </Box>
            </Box>

            <Typography variant="body2" sx={{ color: isLight ? '#475569' : '#94a3b8', fontSize: '0.85rem', lineHeight: 1.7, mb: 2.5, maxWidth: 380 }}>
              Turnkey digital launchpad for newly incorporated businesses. We configure your brand domains, deliver sub-second web storefronts, provision custom domain business email with SPF/DKIM email deliverability, and deploy AI WhatsApp customer receptionists.
            </Typography>

            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                bgcolor: isLight ? '#f1f5f9' : 'rgba(255, 255, 255, 0.03)',
                border: isLight ? '1px solid #e2e8f0' : '1px solid rgba(247, 247, 248, 0.08)',
                px: 1.5,
                py: 0.5,
                borderRadius: '999px',
                mb: 3,
              }}
            >
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#10b981' }} />
              <Typography variant="caption" sx={{ color: isLight ? '#334155' : '#cbd5e1', fontSize: '11px', fontWeight: 500 }}>
                Engineered with ❤️ by{' '}
                <Box
                  component="a"
                  href="https://verveailabs.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ color: '#06b6d4', fontWeight: 600, textDecoration: 'none', '&:hover': { textDecoration: 'underline', color: '#22d3ee' } }}
                >
                  Verve AI Labs ↗
                </Box>
              </Typography>
            </Box>

            {/* Social / Direct Connect */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton
                href="https://wa.me/919415967302"
                target="_blank"
                rel="noopener noreferrer"
                size="small"
                sx={{
                  color: '#10b981',
                  bgcolor: 'rgba(16, 185, 129, 0.1)',
                  border: '1px solid rgba(16, 185, 129, 0.25)',
                  '&:hover': { bgcolor: 'rgba(16, 185, 129, 0.2)' }
                }}
                title="WhatsApp: +91 9415967302"
              >
                <WhatsAppIcon fontSize="small" />
              </IconButton>
              <IconButton
                href="mailto:contact@verveailabs.com"
                size="small"
                sx={{
                  color: '#06b6d4',
                  bgcolor: 'rgba(6, 182, 212, 0.1)',
                  border: '1px solid rgba(6, 182, 212, 0.25)',
                  '&:hover': { bgcolor: 'rgba(6, 182, 212, 0.2)' }
                }}
                title="Email: contact@verveailabs.com"
              >
                <EmailIcon fontSize="small" />
              </IconButton>
              <IconButton
                href="https://linkedin.com/company/verveailabs"
                target="_blank"
                rel="noopener noreferrer"
                size="small"
                sx={{
                  color: '#38bdf8',
                  bgcolor: 'rgba(56, 189, 248, 0.1)',
                  border: '1px solid rgba(56, 189, 248, 0.25)',
                  '&:hover': { bgcolor: 'rgba(56, 189, 248, 0.2)' }
                }}
                title="LinkedIn"
              >
                <LinkedInIcon fontSize="small" />
              </IconButton>
            </Box>
          </Grid>

          {/* Column 1: Solutions */}
          <Grid item xs={6} sm={4} md={2}>
            <Typography variant="subtitle2" sx={{ color: isLight ? '#0f172a' : '#ffffff', fontWeight: 600, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 2 }}>
              Solutions
            </Typography>
            <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <li>
                <Box component="a" href="/#onboarding" sx={{ color: '#06b6d4', fontSize: '0.82rem', textDecoration: 'none', fontWeight: 600, '&:hover': { color: '#22d3ee' } }}>
                  Digital Onboarding →
                </Box>
              </li>
              <li>
                <Box component="a" href="/#ai-solutions" sx={{ color: isLight ? '#475569' : '#94a3b8', fontSize: '0.82rem', textDecoration: 'none', '&:hover': { color: isLight ? '#0f172a' : '#ffffff' } }}>
                  Applied AI Solutions
                </Box>
              </li>
              <li>
                <Box component="a" href="/#ai-solutions" sx={{ color: isLight ? '#475569' : '#94a3b8', fontSize: '0.82rem', textDecoration: 'none', '&:hover': { color: isLight ? '#0f172a' : '#ffffff' } }}>
                  24/7 AI Receptionist
                </Box>
              </li>
              <li>
                <Box component="a" href="/#how-it-works" sx={{ color: isLight ? '#475569' : '#94a3b8', fontSize: '0.82rem', textDecoration: 'none', '&:hover': { color: isLight ? '#0f172a' : '#ffffff' } }}>
                  How It Works
                </Box>
              </li>
              <li>
                <Box component="a" href="/#packages" sx={{ color: isLight ? '#475569' : '#94a3b8', fontSize: '0.82rem', textDecoration: 'none', '&:hover': { color: isLight ? '#0f172a' : '#ffffff' } }}>
                  Pricing Packages
                </Box>
              </li>
            </Box>
          </Grid>

          {/* Column 2: Resources & Products */}
          <Grid item xs={6} sm={4} md={2}>
            <Typography variant="subtitle2" sx={{ color: isLight ? '#0f172a' : '#ffffff', fontWeight: 600, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 2 }}>
              Resources
            </Typography>
            <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <li>
                <Box component={Link} to="/blog" sx={{ color: isLight ? '#475569' : '#94a3b8', fontSize: '0.82rem', textDecoration: 'none', '&:hover': { color: isLight ? '#0f172a' : '#ffffff' } }}>
                  Educational Blog
                </Box>
              </li>
              <li>
                <Box component={Link} to="/portfolio" sx={{ color: isLight ? '#475569' : '#94a3b8', fontSize: '0.82rem', textDecoration: 'none', '&:hover': { color: isLight ? '#0f172a' : '#ffffff' } }}>
                  Client Portfolio
                </Box>
              </li>
              <li>
                <Box component={Link} to="/about" sx={{ color: isLight ? '#475569' : '#94a3b8', fontSize: '0.82rem', textDecoration: 'none', '&:hover': { color: isLight ? '#0f172a' : '#ffffff' } }}>
                  About VerveAI
                </Box>
              </li>
              <li>
                <Box
                  component="a"
                  href="https://gst.verveai.co"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ color: '#06b6d4', fontSize: '0.82rem', textDecoration: 'none', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 0.5, '&:hover': { color: '#22d3ee' } }}
                >
                  <span>GST Portal</span>
                  <OpenInNewIcon sx={{ fontSize: 12 }} />
                </Box>
              </li>
            </Box>
          </Grid>

          {/* Column 3: Studio & Parent Company */}
          <Grid item xs={12} sm={4} md={3}>
            <Typography variant="subtitle2" sx={{ color: isLight ? '#0f172a' : '#ffffff', fontWeight: 600, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 2 }}>
              Parent Studio
            </Typography>
            <Typography variant="body2" sx={{ color: isLight ? '#475569' : '#94a3b8', fontSize: '0.8rem', lineHeight: 1.6, mb: 2 }}>
              Looking for enterprise Agentic AI swarms, multimodal document intelligence, or custom RAG architecture?
            </Typography>
            <Box
              component="a"
              href="https://verveailabs.com"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                color: '#06b6d4',
                fontSize: '0.82rem',
                fontWeight: 600,
                textDecoration: 'none',
                bgcolor: isLight ? 'rgba(6, 182, 212, 0.1)' : 'rgba(6, 182, 212, 0.08)',
                border: isLight ? '1px solid rgba(6, 182, 212, 0.35)' : '1px solid rgba(6, 182, 212, 0.25)',
                px: 1.5,
                py: 0.75,
                borderRadius: '8px',
                '&:hover': {
                  bgcolor: 'rgba(6, 182, 212, 0.16)',
                  color: '#22d3ee',
                }
              }}
            >
              <span>Visit Verve AI Labs Studio</span>
              <OpenInNewIcon sx={{ fontSize: 14 }} />
            </Box>
          </Grid>
        </Grid>

        {/* Bottom Bar */}
        <Box
          sx={{
            mt: 8,
            pt: 4,
            borderTop: isLight ? '1px solid #e2e8f0' : '1px solid rgba(247, 247, 248, 0.08)',
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Typography variant="caption" sx={{ color: isLight ? '#475569' : '#94a3b8', fontSize: '11px', display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'wrap' }}>
            <span>© {new Date().getFullYear()} VerveAI (verveai.co)</span>
            <span>•</span>
            <span>Engineered with ❤️ by</span>
            <Box
              component="a"
              href="https://verveailabs.com"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: isLight ? '#0284c7' : '#22d3ee',
                fontWeight: 600,
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.4,
                '&:hover': { textDecoration: 'underline', color: '#38bdf8' }
              }}
            >
              Verve AI Labs ↗
            </Box>
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <Box component={Link} to="/privacy" sx={{ color: '#64748b', fontSize: '11px', textDecoration: 'none', '&:hover': { color: isLight ? '#0f172a' : '#94a3b8' } }}>
              Privacy Policy
            </Box>
            <Box component={Link} to="/terms" sx={{ color: '#64748b', fontSize: '11px', textDecoration: 'none', '&:hover': { color: isLight ? '#0f172a' : '#94a3b8' } }}>
              Terms of Service
            </Box>
            <Box component={Link} to="/contact" sx={{ color: '#64748b', fontSize: '11px', textDecoration: 'none', '&:hover': { color: isLight ? '#0f172a' : '#94a3b8' } }}>
              Contact
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
